import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useMembers } from "@/hooks/use-members";
import type { QuestionPriority, QuestionStatus } from "@/hooks/use-questions";
import { useCreateQuestion } from "@/hooks/use-questions";
import { useStakeholders } from "@/hooks/use-stakeholders";
import { cn } from "@/lib/utils";

interface AskQuestionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  solutionId: string;
  itemId: string;
  itemExternalKey: string | null;
  itemSummary: string;
  itemSourceId: string | null;
}

const PRIORITY_OPTIONS: ReadonlyArray<{ id: QuestionPriority; label: string }> = [
  { id: "low", label: "Low" },
  { id: "med", label: "Medium" },
  { id: "high", label: "High" },
];

const STATUS_OPTIONS: ReadonlyArray<{ id: QuestionStatus; label: string }> = [
  { id: "open", label: "Open" },
  { id: "answered", label: "Answered" },
];

export function AskQuestionSheet({
  open,
  onOpenChange,
  solutionId,
  itemId,
  itemExternalKey,
  itemSummary,
  itemSourceId,
}: AskQuestionSheetProps) {
  const stakeholders = useStakeholders(solutionId);
  const members = useMembers(solutionId);
  const create = useCreateQuestion(solutionId);

  const [text, setText] = useState("");
  /** Encoded as "member:<userId>" or "stakeholder:<stakeholderId>"; "" means unassigned. */
  const [assignee, setAssignee] = useState("");
  const [priority, setPriority] = useState<QuestionPriority>("med");
  const [status, setStatus] = useState<QuestionStatus>("open");
  const [researchNotes, setResearchNotes] = useState("");
  const [contextNote, setContextNote] = useState("");

  // Reset form whenever the sheet re-opens (so navigating items doesn't keep stale draft)
  useEffect(() => {
    if (open) {
      setText("");
      setAssignee("");
      setPriority("med");
      setStatus("open");
      setResearchNotes("");
      setContextNote("");
    }
  }, [open]);

  const assigneeOptions = useMemo(() => {
    const out: { value: string; kind: "member" | "stakeholder"; name: string; sub: string }[] = [];
    for (const m of members.data ?? []) {
      out.push({
        value: `member:${m.userId}`,
        kind: "member",
        name: m.name ?? m.email,
        sub: m.email,
      });
    }
    for (const s of stakeholders.data ?? []) {
      out.push({
        value: `stakeholder:${s.id}`,
        kind: "stakeholder",
        name: s.name,
        sub: `${s.role}${s.org === "client" ? " · Client" : " · Nymbl"}`,
      });
    }
    return out;
  }, [members.data, stakeholders.data]);

  const peopleLoading = members.isLoading || stakeholders.isLoading;
  const noPeople = !peopleLoading && assigneeOptions.length === 0;

  function submit() {
    if (!text.trim()) return;
    const [kind, id] = assignee.split(":");
    create.mutate(
      {
        text: text.trim(),
        assignedToStakeholderId: kind === "stakeholder" ? (id ?? null) : null,
        assignedToUserId: kind === "member" ? (id ?? null) : null,
        priority,
        status,
        researchNotes: researchNotes.trim() || null,
        contextNote: contextNote.trim() || null,
        relatedItemId: itemId,
        sourceId: itemSourceId,
      },
      {
        onSuccess: () => {
          toast.success("Question sent");
          onOpenChange(false);
        },
      },
    );
  }

  const canSubmit = text.trim().length > 0 && !create.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] flex flex-col">
        <SheetHeader className="border-b border-[var(--color-border)]">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
            Ask a question
          </div>
          <SheetTitle className="font-display text-lg leading-tight mt-1">
            New question{itemExternalKey ? ` · ${itemExternalKey}` : ""}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Compose a question linked to this triage item.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Linked item chip */}
          <div className="rounded-md border border-nymbl-sky bg-nymbl-sky/40 px-3 py-2 text-xs flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
              Linked to
            </span>
            {itemExternalKey && (
              <span className="font-mono tabular-nums text-foreground font-semibold">
                {itemExternalKey}
              </span>
            )}
            <span className="text-foreground/80 truncate">{itemSummary}</span>
          </div>

          <Field label="Question" required>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What needs answering before this can be decided?"
              rows={3}
              className="text-sm"
              autoFocus
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Assign to"
              help={
                noPeople
                  ? "No people yet — invite a teammate or add a stakeholder on the Members page."
                  : undefined
              }
            >
              {peopleLoading ? (
                <Skeleton className="h-9 w-full" />
              ) : noPeople ? (
                <div className="h-9 inline-flex items-center px-3 rounded-md border border-dashed border-[var(--color-border)] text-xs text-muted-foreground italic">
                  Unassigned
                </div>
              ) : (
                <Select value={assignee} onValueChange={setAssignee}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    {assigneeOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className="inline-flex items-center gap-2">
                          <span
                            className={cn(
                              "inline-flex items-center rounded px-1 py-0.5 text-[9px] uppercase tracking-[0.1em] font-semibold",
                              opt.kind === "member"
                                ? "bg-nymbl-sky text-nymbl-navy"
                                : "bg-muted text-foreground/70",
                            )}
                          >
                            {opt.kind === "member" ? "Member" : "Stakeholder"}
                          </span>
                          <span>{opt.name}</span>
                          <span className="text-muted-foreground">— {opt.sub}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field label="Priority">
              <PillGroup options={PRIORITY_OPTIONS} value={priority} onChange={setPriority} />
            </Field>
          </div>

          <Field label="Status">
            <PillGroup options={STATUS_OPTIONS} value={status} onChange={setStatus} />
          </Field>

          <Field label="Research notes" help="Optional — what's been investigated">
            <Textarea
              value={researchNotes}
              onChange={(e) => setResearchNotes(e.target.value)}
              placeholder="Links, findings, or anything you've already dug up."
              rows={3}
              className="text-sm"
            />
          </Field>

          <Field label="Context note" help="Optional context for the assignee">
            <Textarea
              value={contextNote}
              onChange={(e) => setContextNote(e.target.value)}
              placeholder="Why this matters for this scope item."
              rows={2}
              className="text-sm"
            />
          </Field>
        </div>

        <SheetFooter className="border-t border-[var(--color-border)] flex flex-row sm:justify-end gap-2 p-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {create.isPending ? "Sending…" : "Send question"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function Field({
  label,
  required,
  help,
  children,
}: {
  label: string;
  required?: boolean;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-1.5">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </div>
      {children}
      {help && <p className="text-[11px] text-muted-foreground mt-1">{help}</p>}
    </div>
  );
}

function PillGroup<T extends string>({
  options,
  value,
  onChange,
}: {
  options: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex items-center rounded-md border border-[var(--color-border)] p-0.5 bg-card">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={cn(
            "px-3 py-1 text-xs font-medium rounded-[5px] transition-colors",
            value === o.id ? "bg-nymbl-navy text-white" : "text-foreground/70 hover:bg-muted",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
